// File: frontend/src/components/Category/CategoryStats.tsx
// Enhanced stats component with better mobile experience

import React from 'react';
import { StatsGrid, StatItem } from '@/components/common/ui';
import { 
  FolderTree, 
  Eye, 
  EyeOff, 
  Folder, 
  FolderOpen,
  TrendingUp 
} from 'lucide-react';

interface CategoryStatsProps {
  stats: {
    total: number;
    active: number;
    inactive: number;
    parent: number;
    child: number;
  };
  isLoading?: boolean;
}

export const CategoryStats: React.FC<CategoryStatsProps> = ({ stats, isLoading }) => {
  if (isLoading || !stats) {
    return (
      <div className="space-y-4">
        <StatsGrid
          stats={[]}
          isLoading={true}
          loadingItems={5}
          layout="basic"
          columns={{ mobile: 2, tablet: 3, desktop: 5 }}
        />
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-3"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const statItems: StatItem[] = [
    { 
      label: 'Total Categories', 
      value: stats.total || 0, 
      icon: FolderTree,
      color: 'blue'
    },
    { 
      label: 'Active', 
      value: stats.active || 0, 
      icon: Eye,
      color: 'green'
    },
    { 
      label: 'Inactive', 
      value: stats.inactive || 0, 
      icon: EyeOff,
      color: 'red'
    },
    { 
      label: 'Parent Categories', 
      value: stats.parent || 0, 
      icon: FolderOpen,
      color: 'purple'
    },
    { 
      label: 'Sub-categories', 
      value: stats.child || 0, 
      icon: Folder,
      color: 'orange'
    },
  ];

  return (
    <div className="space-y-4">
      {/* Stats Grid */}
      <StatsGrid
        stats={statItems}
        layout="basic"
        columns={{ mobile: 2, tablet: 3, desktop: 5 }}
      />

      {/* Quick Insights */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-900 flex items-center">
            <TrendingUp className="h-4 w-4 mr-2 text-gray-500" />
            Quick Insights
          </h4>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <span className="text-gray-700">Activity Rate</span>
            <span className="font-semibold text-blue-600">
              {stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}%
            </span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
            <span className="text-gray-700">Hierarchy Depth</span>
            <span className="font-semibold text-purple-600">
              {stats.child > 0 ? '2 levels' : '1 level'}
            </span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
            <span className="text-gray-700">Avg. Subcategories</span>
            <span className="font-semibold text-orange-600">
              {stats.parent > 0 ? Math.round((stats.child / stats.parent) * 10) / 10 : 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryStats;